// src/components/CalendarScheduleTab.tsx
// Calendar view of the Flow-Trigger Outlook calendar
// Reads/writes via the sharepoint-proxy calendar actions
// Times stored in UTC by Graph API — displayed in Eastern Time

import React, { useState, useEffect, useCallback } from 'react';
import { hebrewDateCaption } from './DateField';
import {
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Plus,
  Trash2,
  Edit3,
  X,
  Clock,
  Calendar,
  AlertCircle,
  Repeat,
  Check,
} from 'lucide-react';

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

interface CalendarEvent {
  id: string;
  subject: 'First_Load_Sheet_And_Routes' | 'Second_Load_Sheet_And_Routes' | 'Daily_Load_Sheet_And_Routes' | string;
  start: { dateTime: string; timeZone: string };
  end: { dateTime: string; timeZone: string };
  type: 'occurrence' | 'seriesMaster' | 'singleInstance' | 'exception';
  seriesMasterId: string | null;
  isAllDay: boolean;
  recurrence: unknown | null;
}

interface DayEvents {
  [dateKey: string]: CalendarEvent[];
}

type EditMode = 'occurrence' | 'series';

interface EditState {
  event: CalendarEvent;
  mode: EditMode;
  newTime: string;   // HH:MM in ET
  newEndTime: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// Constants
// ═══════════════════════════════════════════════════════════════════════════

const EVENT_LABELS: Record<string, string> = {
  First_Load_Sheet_And_Routes:  'First Send',
  Second_Load_Sheet_And_Routes: 'Second Send',
  Daily_Load_Sheet_And_Routes:  'Daily Send',
};

const EVENT_COLORS: Record<string, { bg: string; border: string; text: string; dot: string }> = {
  First_Load_Sheet_And_Routes: {
    bg:     'bg-blue-500/20',
    border: 'border-blue-500/50',
    text:   'text-blue-300',
    dot:    'bg-blue-400',
  },
  Second_Load_Sheet_And_Routes: {
    bg:     'bg-emerald-500/20',
    border: 'border-emerald-500/50',
    text:   'text-emerald-300',
    dot:    'bg-emerald-400',
  },
  // Single daily batch (route_reports.daily_batch) — only fires while that toggle is on.
  Daily_Load_Sheet_And_Routes: {
    bg:     'bg-amber-500/20',
    border: 'border-amber-500/50',
    text:   'text-amber-300',
    dot:    'bg-amber-400',
  },
};

const DEFAULT_COLORS = {
  bg: 'bg-slate-500/20', border: 'border-slate-500/50', text: 'text-slate-300', dot: 'bg-slate-400',
};

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

const PROXY_URL = '/.netlify/functions/sharepoint-proxy';

// ═══════════════════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════════════════

// Graph returns UTC — convert to Eastern for display
function utcToEastern(utcDateTimeStr: string): Date {
  const utc = new Date(utcDateTimeStr.endsWith('Z') ? utcDateTimeStr : utcDateTimeStr + 'Z');
  return new Date(utc.toLocaleString('en-US', { timeZone: 'America/New_York' }));
}

function formatTime(utcDateTimeStr: string): string {
  const et = utcToEastern(utcDateTimeStr);
  return et.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

// "2026-03-12" local key from a UTC datetime string
function toDateKey(utcDateTimeStr: string): string {
  const et = utcToEastern(utcDateTimeStr);
  const y = et.getFullYear();
  const m = String(et.getMonth() + 1).padStart(2, '0');
  const d = String(et.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function dateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// Return a naive local datetime string — Graph API interprets it using the
// timeZone parameter ('Eastern Standard Time') sent alongside it, so no
// client-side UTC conversion is needed or wanted.
const WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
const DEFAULT_REPEAT_DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];

function easternTimeToUtcISO(dateKey: string, timeHHMM: string): string {
  return `${dateKey}T${timeHHMM}:00`;
}

async function apiRequest<T>(action: string, params: Record<string, unknown> = {}): Promise<T> {
  const response = await fetch(PROXY_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, ...params }),
  });
  const result = await response.json();
  if (!result.success) throw new Error(result.error || 'Request failed');
  return result.data as T;
}

// ═══════════════════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════════════════

const CalendarScheduleTab: React.FC = () => {
  const [currentDate, setCurrentDate] = useState(() => new Date());
  const [events, setEvents] = useState<DayEvents>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [editState, setEditState] = useState<EditState | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ event: CalendarEvent; mode: EditMode } | null>(null);
  const [saving, setSaving] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const emptyNewEvent = {
    subject: 'First_Load_Sheet_And_Routes',
    date: '',
    startTime: '17:05',
    endTime: '17:35',
    // Recurring series: weekly on the chosen days, from `date` until `until` (blank = no end).
    repeat: false,
    days: DEFAULT_REPEAT_DAYS,
    until: '',
  };
  const [newEvent, setNewEvent] = useState(emptyNewEvent);

  // ─── Load events for visible month (+ buffer) ───────────────────────────

  const loadEvents = useCallback(async (date: Date) => {
    setLoading(true);
    setError(null);
    try {
      const start = new Date(date.getFullYear(), date.getMonth(), 1);
      const end   = new Date(date.getFullYear(), date.getMonth() + 2, 1);

      const startDate = dateKey(start);
      const endDate   = dateKey(end);

      const raw = await apiRequest<CalendarEvent[]>('getCalendarEvents', { startDate, endDate });

      // Group by ET date key
      const grouped: DayEvents = {};
      for (const event of raw) {
        const key = toDateKey(event.start.dateTime);
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(event);
      }

      // Sort events within each day by start time
      for (const key of Object.keys(grouped)) {
        grouped[key].sort((a, b) =>
          new Date(a.start.dateTime).getTime() - new Date(b.start.dateTime).getTime()
        );
      }

      setEvents(grouped);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load calendar');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEvents(currentDate);
  }, [currentDate, loadEvents]);

  // ─── Calendar grid helpers ───────────────────────────────────────────────

  const year  = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const firstDay   = new Date(year, month, 1).getDay();     // 0=Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = dateKey(new Date());

  const cells: (number | null)[] = [
    ...Array(firstDay).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];
  // Pad to complete last row
  while (cells.length % 7 !== 0) cells.push(null);

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const goToday   = () => { setCurrentDate(new Date()); loadEvents(new Date()); };

  // ─── Edit handlers ───────────────────────────────────────────────────────

  const openEdit = (event: CalendarEvent, mode: EditMode) => {
    const etTime = utcToEastern(event.start.dateTime);
    const etEnd  = utcToEastern(event.end.dateTime);
    const hh = (n: number) => String(n).padStart(2, '0');
    setEditState({
      event,
      mode,
      newTime:    `${hh(etTime.getHours())}:${hh(etTime.getMinutes())}`,
      newEndTime: `${hh(etEnd.getHours())}:${hh(etEnd.getMinutes())}`,
    });
  };

  const saveEdit = async () => {
    if (!editState) return;
    setSaving(true);
    setError(null);
    try {
      const dk = toDateKey(editState.event.start.dateTime);
      const eventId = editState.mode === 'series' && editState.event.seriesMasterId
        ? editState.event.seriesMasterId
        : editState.event.id;

      await apiRequest('updateCalendarEvent', {
        eventId,
        startDateTime: easternTimeToUtcISO(dk, editState.newTime),
        endDateTime:   easternTimeToUtcISO(dk, editState.newEndTime),
        timeZone: 'Eastern Standard Time',
      });

      setEditState(null);
      await loadEvents(currentDate);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update event');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    setSaving(true);
    setError(null);
    try {
      const eventId = deleteConfirm.mode === 'series' && deleteConfirm.event.seriesMasterId
        ? deleteConfirm.event.seriesMasterId
        : deleteConfirm.event.id;

      await apiRequest('deleteCalendarEvent', { eventId });
      setDeleteConfirm(null);
      setSelectedDay(null);
      await loadEvents(currentDate);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete event');
    } finally {
      setSaving(false);
    }
  };

  const createEvent = async () => {
    if (!newEvent.date) return;
    setSaving(true);
    setError(null);
    try {
      await apiRequest('createCalendarEvent', {
        subject: newEvent.subject,
        startDateTime: easternTimeToUtcISO(newEvent.date, newEvent.startTime),
        endDateTime:   easternTimeToUtcISO(newEvent.date, newEvent.endTime),
        timeZone: 'Eastern Standard Time',
        ...(newEvent.repeat && {
          recurrence: {
            pattern: { type: 'weekly', interval: 1, daysOfWeek: newEvent.days },
            range: newEvent.until
              ? { type: 'endDate', startDate: newEvent.date, endDate: newEvent.until }
              : { type: 'noEnd', startDate: newEvent.date, endDate: '' },
          },
        }),
      });
      setShowCreateModal(false);
      setNewEvent(emptyNewEvent);
      await loadEvents(currentDate);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create event');
    } finally {
      setSaving(false);
    }
  };

  // ─── Render: Day cell ────────────────────────────────────────────────────

  const renderDayCell = (day: number | null, idx: number) => {
    if (!day) {
      return <div key={`empty-${idx}`} className="bg-slate-900/20 border border-slate-800/50 min-h-[90px]" />;
    }

    const key = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const dayEvents = events[key] || [];
    const isToday = key === today;
    const isSelected = key === selectedDay;

    return (
      <div
        key={key}
        onClick={() => setSelectedDay(isSelected ? null : key)}
        className={`min-h-[90px] border cursor-pointer transition-all p-1.5 ${
          isSelected
            ? 'bg-slate-700/60 border-blue-500/60'
            : isToday
            ? 'bg-blue-500/10 border-blue-500/30'
            : 'bg-slate-900/30 border-slate-800/50 hover:bg-slate-800/40'
        }`}
      >
        <div className="flex items-center justify-between mb-1">
          <span className={`text-xs font-semibold w-6 h-6 flex items-center justify-center rounded-full ${
            isToday ? 'bg-blue-500 text-white' : 'text-slate-400'
          }`}>
            {day}
          </span>
          {dayEvents.length > 0 && (
            <span className="text-xs text-slate-500">{dayEvents.length}</span>
          )}
        </div>

        <div className="space-y-0.5">
          {dayEvents.slice(0, 2).map(event => {
            const colors = EVENT_COLORS[event.subject] || DEFAULT_COLORS;
            return (
              <div
                key={event.id}
                className={`text-xs px-1.5 py-0.5 rounded border truncate ${colors.bg} ${colors.border} ${colors.text}`}
              >
                <span className={`inline-block w-1.5 h-1.5 rounded-full ${colors.dot} mr-1`} />
                {formatTime(event.start.dateTime)}
              </div>
            );
          })}
          {dayEvents.length > 2 && (
            <div className="text-xs text-slate-500 px-1">+{dayEvents.length - 2} more</div>
          )}
        </div>
      </div>
    );
  };

  // ─── Render: Day detail panel ────────────────────────────────────────────

  const renderDayDetail = () => {
    if (!selectedDay) return null;
    const dayEvents = events[selectedDay] || [];
    const [y, m, d] = selectedDay.split('-').map(Number);
    const label = new Date(y, m - 1, d).toLocaleDateString('en-US', {
      weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
    });

    return (
      <div className="mt-4 bg-slate-800/50 rounded-xl border border-slate-700/50 p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-white flex items-center gap-2">
            <Calendar size={16} className="text-blue-400" />
            {label}
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setNewEvent(e => ({ ...e, date: selectedDay }));
                setShowCreateModal(true);
              }}
              className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus size={14} />
              Add send
            </button>
            <button onClick={() => setSelectedDay(null)} className="p-1 text-slate-500 hover:text-white">
              <X size={16} />
            </button>
          </div>
        </div>

        {dayEvents.length === 0 ? (
          <p className="text-slate-500 text-sm py-4 text-center">No sends scheduled — this day is off</p>
        ) : (
          <div className="space-y-3">
            {dayEvents.map(event => {
              const colors = EVENT_COLORS[event.subject] || DEFAULT_COLORS;
              const label = EVENT_LABELS[event.subject] || event.subject;
              return (
                <div key={event.id} className={`p-3 rounded-lg border ${colors.bg} ${colors.border}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className={`font-medium text-sm ${colors.text} flex items-center gap-2`}>
                        <span className={`w-2 h-2 rounded-full ${colors.dot}`} />
                        {label}
                      </div>
                      <div className="text-white text-sm mt-1 flex items-center gap-1">
                        <Clock size={12} className="text-slate-400" />
                        {formatTime(event.start.dateTime)} → {formatTime(event.end.dateTime)} ET
                      </div>
                      {event.type === 'occurrence' && (
                        <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                          <Repeat size={10} />
                          Recurring series
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-1 ml-2">
                      <button
                        onClick={() => openEdit(event, 'occurrence')}
                        className="p-1.5 rounded-lg bg-slate-700/50 text-slate-400 hover:text-white hover:bg-slate-600 transition-colors"
                        title="Edit this occurrence only"
                      >
                        <Edit3 size={14} />
                      </button>
                      {event.seriesMasterId && (
                        <button
                          onClick={() => openEdit(event, 'series')}
                          className="p-1.5 rounded-lg bg-slate-700/50 text-slate-400 hover:text-white hover:bg-slate-600 transition-colors"
                          title="Edit all future occurrences"
                        >
                          <Repeat size={14} />
                        </button>
                      )}
                      <button
                        onClick={() => setDeleteConfirm({ event, mode: 'occurrence' })}
                        className="p-1.5 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
                        title="Skip this day"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // ─── Render: Edit Modal ──────────────────────────────────────────────────

  const renderEditModal = () => {
    if (!editState) return null;
    const colors = EVENT_COLORS[editState.event.subject] || DEFAULT_COLORS;
    const label = EVENT_LABELS[editState.event.subject] || editState.event.subject;

    return (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
        <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl">
          <div className="p-5 border-b border-slate-700">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-white">Edit Send Time</h3>
              <button onClick={() => setEditState(null)} className="p-1 text-slate-400 hover:text-white">
                <X size={18} />
              </button>
            </div>
            <div className={`mt-2 text-sm ${colors.text} flex items-center gap-2`}>
              <span className={`w-2 h-2 rounded-full ${colors.dot}`} />
              {label}
              {editState.mode === 'series' && (
                <span className="text-slate-500 flex items-center gap-1">
                  <Repeat size={12} /> all future occurrences
                </span>
              )}
              {editState.mode === 'occurrence' && (
                <span className="text-slate-500">this day only</span>
              )}
            </div>
          </div>

          <div className="p-5 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-slate-400 mb-1.5">Start time (ET)</label>
                <input
                  type="time"
                  value={editState.newTime}
                  onChange={e => setEditState(s => s ? { ...s, newTime: e.target.value } : s)}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1.5">End time (ET)</label>
                <input
                  type="time"
                  value={editState.newEndTime}
                  onChange={e => setEditState(s => s ? { ...s, newEndTime: e.target.value } : s)}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {editState.mode === 'series' && (
              <div className="px-3 py-2 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-400 text-xs">
                This will update the time for all future occurrences of this send.
              </div>
            )}
          </div>

          <div className="p-5 border-t border-slate-700 flex justify-end gap-3">
            <button
              onClick={() => setEditState(null)}
              className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={saveEdit}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {saving ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
              Save changes
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ─── Render: Delete Confirm Modal ────────────────────────────────────────

  const renderDeleteModal = () => {
    if (!deleteConfirm) return null;
    const label = EVENT_LABELS[deleteConfirm.event.subject] || deleteConfirm.event.subject;

    return (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
        <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-sm shadow-2xl p-6">
          <h3 className="font-semibold text-white mb-2">Confirm cancellation</h3>
          <p className="text-slate-400 text-sm mb-4">
            Are you sure you want to cancel <strong className="text-white">{label}</strong> on{' '}
            <strong className="text-white">{formatTime(deleteConfirm.event.start.dateTime)} ET</strong>?
          </p>

          <div className="space-y-2 mb-5">
            <button
              onClick={() => setDeleteConfirm(d => d ? { ...d, mode: 'occurrence' } : d)}
              className={`w-full p-3 rounded-lg border text-left text-sm transition-colors ${
                deleteConfirm.mode === 'occurrence'
                  ? 'bg-red-500/20 border-red-500/50 text-red-300'
                  : 'bg-slate-700/50 border-slate-600 text-slate-400 hover:border-slate-500'
              }`}
            >
              <strong className="block text-white">This day only</strong>
              Skip this one occurrence
            </button>
            {deleteConfirm.event.seriesMasterId && (
              <button
                onClick={() => setDeleteConfirm(d => d ? { ...d, mode: 'series' } : d)}
                className={`w-full p-3 rounded-lg border text-left text-sm transition-colors ${
                  deleteConfirm.mode === 'series'
                    ? 'bg-red-500/20 border-red-500/50 text-red-300'
                    : 'bg-slate-700/50 border-slate-600 text-slate-400 hover:border-slate-500'
                }`}
              >
                <strong className="block text-white">This and all future</strong>
                Remove all remaining occurrences
              </button>
            )}
          </div>

          <div className="flex justify-end gap-3">
            <button
              onClick={() => setDeleteConfirm(null)}
              className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
            >
              Keep it
            </button>
            <button
              onClick={confirmDelete}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              {saving ? <RefreshCw size={14} className="animate-spin" /> : <Trash2 size={14} />}
              Cancel send
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ─── Render: Create Modal ────────────────────────────────────────────────

  const renderCreateModal = () => {
    if (!showCreateModal) return null;

    return (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
        <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl">
          <div className="p-5 border-b border-slate-700 flex items-center justify-between">
            <h3 className="font-semibold text-white">{newEvent.repeat ? 'Add Recurring Send' : 'Add One-Off Send'}</h3>
            <button onClick={() => setShowCreateModal(false)} className="p-1 text-slate-400 hover:text-white">
              <X size={18} />
            </button>
          </div>

          <div className="p-5 space-y-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1.5">Send type</label>
              <select
                value={newEvent.subject}
                onChange={e => setNewEvent(n => ({ ...n, subject: e.target.value }))}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="First_Load_Sheet_And_Routes">First Send (Load Sheet + Routes)</option>
                <option value="Second_Load_Sheet_And_Routes">Second Send (Load Sheet + Routes)</option>
                <option value="Daily_Load_Sheet_And_Routes">Daily Send (single daily batch, all orders incl. 003)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm text-slate-400 mb-1.5">{newEvent.repeat ? 'Starts on' : 'Date'}</label>
              <input
                type="date"
                value={newEvent.date}
                onChange={e => setNewEvent(n => ({ ...n, date: e.target.value }))}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500"
              />
              {hebrewDateCaption(newEvent.date) && (
                <span className="block mt-0.5 text-[11px] text-slate-500">{hebrewDateCaption(newEvent.date)}</span>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-slate-400 mb-1.5">Start time (ET)</label>
                <input
                  type="time"
                  value={newEvent.startTime}
                  onChange={e => setNewEvent(n => ({ ...n, startTime: e.target.value }))}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1.5">End time (ET)</label>
                <input
                  type="time"
                  value={newEvent.endTime}
                  onChange={e => setNewEvent(n => ({ ...n, endTime: e.target.value }))}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="space-y-3">
              <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={newEvent.repeat}
                  onChange={e => setNewEvent(n => ({ ...n, repeat: e.target.checked }))}
                  className="accent-blue-500"
                />
                Repeat weekly
              </label>
              {newEvent.repeat && (
                <>
                  <div className="flex flex-wrap gap-1.5">
                    {WEEKDAYS.map(d => {
                      const on = newEvent.days.includes(d);
                      return (
                        <button
                          key={d}
                          type="button"
                          onClick={() => setNewEvent(n => ({
                            ...n,
                            days: on ? n.days.filter(x => x !== d) : WEEKDAYS.filter(x => x === d || n.days.includes(x)),
                          }))}
                          className={`px-2.5 py-1 rounded-md text-xs border transition-colors ${on
                            ? 'bg-blue-600/30 border-blue-500/60 text-blue-200'
                            : 'bg-slate-700 border-slate-600 text-slate-400 hover:text-white'}`}
                        >
                          {d.slice(0, 3).replace(/^./, c => c.toUpperCase())}
                        </button>
                      );
                    })}
                  </div>
                  <div>
                    <label className="block text-sm text-slate-400 mb-1.5">Ends on (optional)</label>
                    <input
                      type="date"
                      value={newEvent.until}
                      min={newEvent.date || undefined}
                      onChange={e => setNewEvent(n => ({ ...n, until: e.target.value }))}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500"
                    />
                    {hebrewDateCaption(newEvent.until) && (
                <span className="block mt-0.5 text-[11px] text-slate-500">{hebrewDateCaption(newEvent.until)}</span>
              )}
                    <p className="text-xs text-slate-500 mt-1">Leave blank to repeat with no end date.</p>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="p-5 border-t border-slate-700 flex justify-end gap-3">
            <button onClick={() => setShowCreateModal(false)} className="px-4 py-2 text-slate-400 hover:text-white">
              Cancel
            </button>
            <button
              onClick={createEvent}
              disabled={saving || !newEvent.date || (newEvent.repeat && newEvent.days.length === 0)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {saving ? <RefreshCw size={14} className="animate-spin" /> : <Plus size={14} />}
              Add send
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ─── Main render ─────────────────────────────────────────────────────────

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-white">Send Schedule</h2>
          <p className="text-sm text-slate-400 mt-0.5">
            Synced with <span className="text-blue-400">Devash-Flow-Trigger</span> calendar · all times Eastern
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Legend */}
          <div className="hidden sm:flex items-center gap-4 mr-4">
            {Object.entries(EVENT_LABELS).map(([key, label]) => {
              const colors = EVENT_COLORS[key];
              return (
                <div key={key} className="flex items-center gap-1.5 text-xs text-slate-400">
                  <span className={`w-2 h-2 rounded-full ${colors.dot}`} />
                  {label}
                </div>
              );
            })}
          </div>
          <button
            onClick={() => { setNewEvent(e => ({ ...e, date: today })); setShowCreateModal(true); }}
            className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus size={14} />
            Add send
          </button>
          <button
            onClick={() => loadEvents(currentDate)}
            disabled={loading}
            className="p-2 bg-slate-700 text-slate-400 hover:text-white rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 flex items-center gap-3 text-sm">
          <AlertCircle size={16} />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">
            <X size={14} />
          </button>
        </div>
      )}

      {/* Month nav */}
      <div className="flex items-center justify-between mb-4">
        <button onClick={prevMonth} className="p-2 rounded-lg bg-slate-800/50 hover:bg-slate-700/50 text-slate-400 hover:text-white transition-colors">
          <ChevronLeft size={18} />
        </button>
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-white">
            {MONTH_NAMES[month]} {year}
          </h3>
          <button onClick={goToday} className="text-xs px-2.5 py-1 bg-slate-700/50 text-slate-400 hover:text-white rounded-md transition-colors">
            Today
          </button>
        </div>
        <button onClick={nextMonth} className="p-2 rounded-lg bg-slate-800/50 hover:bg-slate-700/50 text-slate-400 hover:text-white transition-colors">
          <ChevronRight size={18} />
        </button>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 mb-1">
        {DAY_NAMES.map(d => (
          <div key={d} className="text-center text-xs font-medium text-slate-500 py-1.5">
            {d}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className={`grid grid-cols-7 gap-px bg-slate-800/30 rounded-xl overflow-hidden border border-slate-700/50 ${loading ? 'opacity-60' : ''}`}>
        {cells.map((day, idx) => renderDayCell(day, idx))}
      </div>

      {/* Selected day detail */}
      {renderDayDetail()}

      {/* Modals */}
      {renderEditModal()}
      {renderDeleteModal()}
      {renderCreateModal()}
    </div>
  );
};

export default CalendarScheduleTab;